import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import {
  X,
  TrendingUp,
  TrendingDown,
  Minus,
  Search,
  BarChart3,
  Droplets,
  Gauge,
  DollarSign,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { useProductionCalculator } from '../hooks/useProductionCalculator';

interface WellComparisonProps {
  wells: any[];
  onClose: () => void;
  preselectedWells?: string[];
  isDarkMode?: boolean;
  /** Optional calculated production values for each well */
  calculatedProduction?: Map<string, {
    netOilBBL: number;
    grossWaterBBL: number;
    waterCutPercent: number;
    oilVsYesterdayPercent?: number;
  }>;
  /** Whether to prefer calculated values over raw well values */
  useCalculatedValues?: boolean;
}

export default function WellComparison({
  wells,
  onClose,
  preselectedWells = [],
  isDarkMode = false,
  calculatedProduction: externalCalcProduction,
  useCalculatedValues = false,
}: WellComparisonProps) {
  const [selectedWells, setSelectedWells] = useState<Set<string>>(
    new Set(preselectedWells)
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [comparisonMetric, setComparisonMetric] = useState<'oil' | 'water' | 'gas' | 'all'>('all');
  const [viewMode, setViewMode] = useState<'selection' | 'comparison'>('selection'); // NEW: Track viewing mode
  
  // Production calculator hook - use utility functions for display calculations
  const { convertInchesToBarrels, calculateWaterCutPercent, getBblPerInchForTank } = useProductionCalculator();
  
  // Calculate production values internally when not provided externally
  // Uses utility functions for simple tank-to-BBL conversions for display
  const calculatedProduction = useMemo(() => {
    if (externalCalcProduction) return externalCalcProduction;
    
    const results = new Map<string, {
      netOilBBL: number;
      grossWaterBBL: number;
      waterCutPercent: number;
      oilVsYesterdayPercent?: number;
    }>();
    
    wells.forEach(well => {
      try {
        const tankData = well.latestEntry?.tankGauges || well.tankGauges;
        if (tankData) {
          // Calculate oil from tank gauges
          let totalOilBBL = 0;
          let totalWaterInOilBBL = 0;
          (tankData.oil || []).forEach((t: any) => {
            const currentInches = t.topGauge || t.inches || 0;
            const previousInches = t.previousTopGauge || t.bottomGauge || 0;
            const bblPerInch = t.bblPerInch || 3.57;
            const levelChange = currentInches - previousInches;
            const barrelsProduced = levelChange * bblPerInch + (t.barrelsHauled || 0);
            totalOilBBL += Math.max(0, barrelsProduced);
            
            // Account for water in oil tanks (Kolor Kut)
            if (t.kolorKut?.waterCutInches > 0) {
              totalWaterInOilBBL += t.kolorKut.waterCutInches * bblPerInch;
            }
          });
          
          // Calculate water from water tanks
          let totalWaterBBL = 0;
          (tankData.water || []).forEach((t: any) => {
            const currentInches = t.topGauge || t.inches || 0;
            const previousInches = t.previousTopGauge || t.bottomGauge || 0;
            const bblPerInch = t.bblPerInch || 3.57;
            const levelChange = currentInches - previousInches;
            const barrelsProduced = levelChange * bblPerInch + (t.barrelsHauled || 0);
            totalWaterBBL += Math.max(0, barrelsProduced);
          });
          
          // Net oil excludes water detected in oil tanks
          const netOilBBL = Math.max(0, totalOilBBL - totalWaterInOilBBL);
          const grossWaterBBL = totalWaterBBL + totalWaterInOilBBL;
          const waterCutPercent = calculateWaterCutPercent(netOilBBL, grossWaterBBL);
          
          // Calculate trend vs yesterday if available
          let oilVsYesterdayPercent: number | undefined;
          const yesterdayOil =
            typeof well?.latestEntry?.yesterdayOil === 'number'
            ? well.latestEntry.yesterdayOil
            : typeof well?.previousEntry?.oilProduction === 'number'
              ? well.previousEntry.oilProduction
              : typeof well?.oilProductionYesterday === 'number'
              ? well.oilProductionYesterday
              : undefined;

          if (typeof yesterdayOil === 'number' && yesterdayOil > 0 && netOilBBL > 0) {
            oilVsYesterdayPercent = ((netOilBBL - yesterdayOil) / yesterdayOil) * 100;
          }

          if (netOilBBL > 0 || grossWaterBBL > 0) {
            results.set(well.id, {
              netOilBBL: Math.round(netOilBBL * 100) / 100,
              grossWaterBBL: Math.round(grossWaterBBL * 100) / 100,
              waterCutPercent: Math.round(waterCutPercent * 10) / 10,
              oilVsYesterdayPercent,
            });
          }
        }
      } catch (err) {
        // Fall back to raw values - no entry in map means use well.oilProduction etc.
      }
    });
    
    return results.size > 0 ? results : undefined;
  }, [wells, externalCalcProduction, calculateWaterCutPercent]);

  // Helper function to get production values
  const getWellProduction = (well: any) => {
    if (calculatedProduction) {
      const calc = calculatedProduction.get(well.id);
      if (calc) {
        return {
          oil: calc.netOilBBL,
          water: calc.grossWaterBBL,
          waterCut: calc.waterCutPercent,
        };
      }
    }
    const oil = well.oilProduction || 0;
    const water = well.waterProduction || 0;
    return {
      oil,
      water,
      waterCut: oil + water > 0 ? (water / (oil + water)) * 100 : 0,
    };
  };

  // Filter wells by search
  const filteredWells = wells.filter(well =>
    well.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    well.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected wells data
  const selectedWellsData = wells.filter(well => selectedWells.has(well.id));

  const toggleWellSelection = (wellId: string) => {
    const newSelected = new Set(selectedWells);
    if (newSelected.has(wellId)) {
      newSelected.delete(wellId);
    } else {
      if (newSelected.size >= 6) {
        toast.warning('Maximum 6 wells can be compared at once');
        return;
      }
      newSelected.add(wellId);
    }
    setSelectedWells(newSelected);
  };

  const handleCompare = () => {
    if (selectedWells.size < 2) {
      toast.error('Please select at least 2 wells to compare');
      return;
    }
    setViewMode('comparison');
  };

  const handleBackToSelection = () => {
    setViewMode('selection');
  };

  // Calculate comparison metrics
  const comparisonStats = useMemo(() => {
    if (selectedWellsData.length === 0) return null;

    const stats = selectedWellsData.map(well => {
      const prod = getWellProduction(well);
      return {
        id: well.id,
        name: well.name,
        oilProduction: prod.oil,
        waterProduction: prod.water,
        gasVolume: well.staticPressure || 0,
        waterCut: prod.waterCut,
        revenue: prod.oil * 75, // Assuming $75/barrel
        efficiency: prod.oil && well.maintenanceCost
          ? prod.oil / well.maintenanceCost
          : 0,
      };
    });

    // Calculate averages
    const avgOil = stats.reduce((sum, s) => sum + s.oilProduction, 0) / stats.length;
    const avgWater = stats.reduce((sum, s) => sum + s.waterProduction, 0) / stats.length;
    const avgGas = stats.reduce((sum, s) => sum + s.gasVolume, 0) / stats.length;
    const avgWaterCut = stats.reduce((sum, s) => sum + s.waterCut, 0) / stats.length;

    // Find best and worst performers
    const bestOilWell = stats.reduce((best, current) =>
      current.oilProduction > best.oilProduction ? current : best
    );
    const worstOilWell = stats.reduce((worst, current) =>
      current.oilProduction < worst.oilProduction ? current : worst
    );

    return {
      stats,
      averages: {
        oil: avgOil,
        water: avgWater,
        gas: avgGas,
        waterCut: avgWaterCut,
      },
      bestOilWell,
      worstOilWell,
    };
  }, [selectedWellsData]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!comparisonStats) return [];

    return comparisonStats.stats.map(stat => ({
      name: stat.name.length > 10 ? stat.name.substring(0, 10) + '...' : stat.name,
      Oil: Math.round(stat.oilProduction),
      Water: Math.round(stat.waterProduction),
      Gas: Math.round(stat.gasVolume),
      'Water Cut %': Math.round(stat.waterCut),
      Revenue: Math.round(stat.revenue),
    }));
  }, [comparisonStats]);

  const getPercentageDiff = (value: number, average: number) => {
    if (average === 0) return 0;
    return ((value - average) / average) * 100;
  };

  const getDiffBadge = (value: number, average: number, higherIsBetter = true) => {
    const diff = getPercentageDiff(value, average);
    const isPositive = higherIsBetter ? diff > 0 : diff < 0;

    if (Math.abs(diff) < 5) {
      return (
        <Badge variant="outline" className="bg-gray-100 text-gray-700">
          <Minus className="h-3 w-3 mr-1" />
          {Math.abs(diff).toFixed(1)}%
        </Badge>
      );
    }

    return isPositive ? (
      <Badge className="bg-green-100 text-green-800 border-green-200">
        <TrendingUp className="h-3 w-3 mr-1" />
        +{Math.abs(diff).toFixed(1)}%
      </Badge>
    ) : (
      <Badge className="bg-red-100 text-red-800 border-red-200">
        <TrendingDown className="h-3 w-3 mr-1" />
        -{Math.abs(diff).toFixed(1)}%
      </Badge>
    );
  };

  return (
    <div className={`fixed inset-0 z-50 flex flex-col ${isDarkMode ? 'bg-slate-950' : 'bg-white'}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-4 py-4 shadow-lg">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-6 w-6" />
              <h2 className="text-xl font-bold">Well Comparison</h2>
            </div>
            <p className="text-sm text-purple-100 mt-1">
              Compare up to 6 wells side-by-side
            </p>
          </div>

          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20 h-10 w-10"
          >
            <X className="h-6 w-6" />
          </Button>
        </div>

        <div className="flex items-center space-x-2 text-sm">
          <span className="text-purple-100">Selected:</span>
          <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
            {selectedWells.size} / 6 wells
          </Badge>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {viewMode === 'selection' ? (
          // Well Selection View
          <div className="p-4 space-y-4">
            <Card>
              <CardContent className="p-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search wells..."
                    className="pl-10 h-12"
                  />
                </div>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-700 px-1">
                Select wells to compare (up to 6)
              </p>
              {filteredWells.map(well => (
                <Card
                  key={well.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${selectedWells.has(well.id) ? 'border-purple-500 border-2 bg-purple-50' : ''
                    }`}
                  onClick={() => toggleWellSelection(well.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start space-x-3">
                      <div className="pt-1">
                        <Checkbox
                          checked={selectedWells.has(well.id)}
                          onCheckedChange={() => toggleWellSelection(well.id)}
                        />
                      </div>

                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{well.name}</h3>
                        <p className="text-sm text-gray-600">{well.location}</p>

                        <div className="grid grid-cols-3 gap-2 mt-3">
                          <div className="bg-green-50 rounded px-2 py-1.5">
                            <p className="text-xs text-green-700">Oil</p>
                            <p className="font-semibold text-green-900">
                              {(well.oilProduction || 0).toFixed(1)}
                            </p>
                          </div>
                          <div className="bg-blue-50 rounded px-2 py-1.5">
                            <p className="text-xs text-blue-700">Water</p>
                            <p className="font-semibold text-blue-900">
                              {(well.waterProduction || 0).toFixed(1)}
                            </p>
                          </div>
                          <div className="bg-purple-50 rounded px-2 py-1.5">
                            <p className="text-xs text-purple-700">Gas</p>
                            <p className="font-semibold text-purple-900">
                              {(well.staticPressure || 0).toFixed(1)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-4">
              <Button
                onClick={handleCompare}
                className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white"
              >
                Compare Wells
              </Button>
            </div>
          </div>
        ) : (
          // Comparison View
          <div className="p-4 space-y-4">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-3">
              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-green-700">Best Performer</p>
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  </div>
                  <p className="font-bold text-green-900 truncate">
                    {comparisonStats?.bestOilWell.name}
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    {comparisonStats?.bestOilWell.oilProduction.toFixed(1)} BPD
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-red-700">Needs Attention</p>
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  </div>
                  <p className="font-bold text-red-900 truncate">
                    {comparisonStats?.worstOilWell.name}
                  </p>
                  <p className="text-xs text-red-700 mt-1">
                    {comparisonStats?.worstOilWell.oilProduction.toFixed(1)} BPD
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Metric Selector */}
            <Card>
              <CardContent className="p-3">
                <div className="grid grid-cols-4 gap-2">
                  <button
                    onClick={() => setComparisonMetric('all')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${comparisonMetric === 'all'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700'
                      }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setComparisonMetric('oil')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${comparisonMetric === 'oil'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700'
                      }`}
                  >
                    Oil
                  </button>
                  <button
                    onClick={() => setComparisonMetric('water')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${comparisonMetric === 'water'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700'
                      }`}
                  >
                    Water
                  </button>
                  <button
                    onClick={() => setComparisonMetric('gas')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${comparisonMetric === 'gas'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700'
                      }`}
                  >
                    Gas
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Production Comparison Chart */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Production Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {(comparisonMetric === 'all' || comparisonMetric === 'oil') && (
                        <Bar dataKey="Oil" fill="#22c55e" />
                      )}
                      {(comparisonMetric === 'all' || comparisonMetric === 'water') && (
                        <Bar dataKey="Water" fill="#3b82f6" />
                      )}
                      {(comparisonMetric === 'all' || comparisonMetric === 'gas') && (
                        <Bar dataKey="Gas" fill="#a855f7" />
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Water Cut Comparison */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Water Cut Percentage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="horizontal">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
                      <Tooltip />
                      <Bar dataKey="Water Cut %" fill="#f59e0b" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Detailed Comparison Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Detailed Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {comparisonStats?.stats.map(stat => (
                    <div key={stat.id} className="border-b pb-4 last:border-b-0">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-900">{stat.name}</h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedWells(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(stat.id);
                              return newSet;
                            });
                          }}
                          className="h-7 w-7 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        {/* Oil Production */}
                        <div className="bg-green-50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center space-x-1">
                              <Droplets className="h-4 w-4 text-green-600" />
                              <span className="text-xs text-green-700">Oil</span>
                            </div>
                            {getDiffBadge(stat.oilProduction, comparisonStats.averages.oil, true)}
                          </div>
                          <p className="text-lg font-bold text-green-900">
                            {stat.oilProduction.toFixed(1)}
                          </p>
                          <p className="text-xs text-green-600">BPD</p>
                        </div>

                        {/* Water Production */}
                        <div className="bg-blue-50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center space-x-1">
                              <Droplets className="h-4 w-4 text-blue-600" />
                              <span className="text-xs text-blue-700">Water</span>
                            </div>
                            {getDiffBadge(stat.waterProduction, comparisonStats.averages.water, false)}
                          </div>
                          <p className="text-lg font-bold text-blue-900">
                            {stat.waterProduction.toFixed(1)}
                          </p>
                          <p className="text-xs text-blue-600">BPD</p>
                        </div>

                        {/* Gas Volume */}
                        <div className="bg-purple-50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center space-x-1">
                              <Gauge className="h-4 w-4 text-purple-600" />
                              <span className="text-xs text-purple-700">Gas</span>
                            </div>
                            {getDiffBadge(stat.gasVolume, comparisonStats.averages.gas, true)}
                          </div>
                          <p className="text-lg font-bold text-purple-900">
                            {stat.gasVolume.toFixed(1)}
                          </p>
                          <p className="text-xs text-purple-600">MCF</p>
                        </div>

                        {/* Water Cut */}
                        <div className="bg-orange-50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-orange-700">Water Cut</span>
                            {getDiffBadge(stat.waterCut, comparisonStats.averages.waterCut, false)}
                          </div>
                          <p className="text-lg font-bold text-orange-900">
                            {stat.waterCut.toFixed(1)}
                          </p>
                          <p className="text-xs text-orange-600">%</p>
                        </div>
                      </div>

                      {/* Revenue Estimate */}
                      <div className="mt-3 bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <DollarSign className="h-4 w-4 text-gray-600" />
                          <span className="text-sm text-gray-700">Est. Daily Revenue</span>
                        </div>
                        <p className="text-lg font-bold text-gray-900">
                          ${stat.revenue.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Average Comparison */}
            <Card className="bg-gradient-to-br from-gray-50 to-gray-100">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Group Averages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-1">Avg Oil</p>
                    <p className="text-xl font-bold text-green-600">
                      {comparisonStats?.averages.oil.toFixed(1)}
                    </p>
                    <p className="text-xs text-gray-500">BPD</p>
                  </div>

                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-1">Avg Water</p>
                    <p className="text-xl font-bold text-blue-600">
                      {comparisonStats?.averages.water.toFixed(1)}
                    </p>
                    <p className="text-xs text-gray-500">BPD</p>
                  </div>

                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-1">Avg Gas</p>
                    <p className="text-xl font-bold text-purple-600">
                      {comparisonStats?.averages.gas.toFixed(1)}
                    </p>
                    <p className="text-xs text-gray-500">MCF</p>
                  </div>

                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-1">Avg Water Cut</p>
                    <p className="text-xl font-bold text-orange-600">
                      {comparisonStats?.averages.waterCut.toFixed(1)}
                    </p>
                    <p className="text-xs text-gray-500">%</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Insights */}
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center space-x-2">
                  <AlertTriangle className="h-5 w-5 text-blue-600" />
                  <span>Key Insights</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-blue-900">
                  <li className="flex items-start space-x-2">
                    <ArrowRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>{comparisonStats?.bestOilWell.name}</strong> produces{' '}
                      {comparisonStats && (
                        Math.abs(
                          ((comparisonStats.bestOilWell.oilProduction -
                            comparisonStats.worstOilWell.oilProduction) /
                            comparisonStats.worstOilWell.oilProduction) *
                          100
                        ).toFixed(0)
                      )}
                      % more oil than <strong>{comparisonStats?.worstOilWell.name}</strong>
                    </span>
                  </li>

                  {comparisonStats && comparisonStats.averages.waterCut > 50 && (
                    <li className="flex items-start space-x-2">
                      <ArrowRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>
                        High average water cut ({comparisonStats.averages.waterCut.toFixed(1)}%)
                        - consider water management strategies
                      </span>
                    </li>
                  )}

                  <li className="flex items-start space-x-2">
                    <ArrowRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>
                      Total estimated daily revenue: $
                      {comparisonStats?.stats
                        .reduce((sum, s) => sum + s.revenue, 0)
                        .toLocaleString()}
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <div className="mt-4">
              <Button
                onClick={handleBackToSelection}
                variant="outline"
                className="w-full h-12"
              >
                Select Different Wells
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {viewMode === 'selection' ? (
        <div className="bg-white border-t px-4 py-4 space-y-2">
          {selectedWells.size > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-2">
              <p className="text-sm text-purple-900">
                <strong>{selectedWells.size}</strong> {selectedWells.size === 1 ? 'well' : 'wells'} selected
              </p>
              <p className="text-xs text-purple-700 mt-1">
                {selectedWells.size < 2 ? 'Select at least 2 wells to compare' : 'Ready to compare!'}
              </p>
            </div>
          )}
          <div className="flex space-x-2">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 h-12"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCompare}
              disabled={selectedWells.size < 2}
              className="flex-1 h-12 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Compare {selectedWells.size > 0 && `(${selectedWells.size})`}
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-white border-t px-4 py-4">
          <Button
            onClick={handleBackToSelection}
            variant="outline"
            className="w-full h-12"
          >
            ← Back to Selection
          </Button>
        </div>
      )}
    </div>
  );
}